import { AuthorizationHelper, CommonAPIHelper } from '@/_helpers';
import { IBaseMetadata, IURMRole, IUserRole, ORGANISATION_TYPE, OrganisationApiItem } from '@/_types';
import { UserRoleService } from '@/features/administration/roles/data/user-role.service';
import { UserManagementService } from '@/features/administration/usermanagement/data/user-management.service';
import { NovatiqMessageService } from '@/layouts/service/message.service';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Button } from 'primeng/button';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { Listbox } from 'primeng/listbox';
import { StepperModule } from 'primeng/stepper';
import { catchError, finalize, of } from 'rxjs';

const SHADOW_ORG_TYPE_ORDER: Record<string, number> = {
  [ORGANISATION_TYPE.TELCO]: 1,
  [ORGANISATION_TYPE.AGENCY]: 2,
  [ORGANISATION_TYPE.BUY_SIDE]: 3,
  [ORGANISATION_TYPE.BUYSIDE_PUBLISHER]: 4,
  [ORGANISATION_TYPE.DISTRIBUTION_PUBLISHER]: 5,
  [ORGANISATION_TYPE.BUYSIDE_SSP]: 6,
  [ORGANISATION_TYPE.DISTRIBUTION_SSP]: 7
};

@Component({
  selector: 'novatiq-shadow-customer-organisation-dialog',
  templateUrl: './shadow-customer-organisation-dialog.component.html',
  styleUrl: './shadow-customer-organisation-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, StepperModule, Listbox, Button, TranslatePipe]
})
export class ShadowCustomerOrganisationDialogComponent implements OnInit {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly userManagementService = inject(UserManagementService);
  private readonly userRoleService = inject(UserRoleService);
  private readonly commonApiHelper = inject(CommonAPIHelper);
  private readonly authorizationHelper = inject(AuthorizationHelper);
  private readonly messageService = inject(NovatiqMessageService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly orgTypes = signal<IBaseMetadata[]>([]);
  readonly activeStep = signal<1 | 2 | 3>(1);
  readonly selectedOrgType = signal<string | null>(null);
  readonly selectedOrganisationId = signal<string | null>(null);
  readonly selectedRoleId = signal<number | null>(null);
  readonly organisations = signal<OrganisationApiItem[]>([]);
  readonly roles = signal<IUserRole[]>([]);
  readonly loadingOrgTypes = signal(true);
  readonly loadingOrganisations = signal(false);
  readonly loadingRoles = signal(false);
  readonly submitting = signal(false);
  readonly apiError = signal<string | null>(null);

  readonly canProceedStep1 = computed(() => this.selectedOrgType() !== null);
  readonly canProceedStep2 = computed(() => this.selectedOrganisationId() !== null);
  readonly canProceedStep3 = computed(() => this.selectedRoleId() !== null);

  readonly listboxScrollHeight = '14rem';

  ngOnInit(): void {
    this.commonApiHelper
      .getOrganisationTypes()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loadingOrgTypes.set(false))
      )
      .subscribe({
        next: (metadata) => {
          const customerOrgTypes = (metadata.metadata ?? []).filter((item) => item.value !== ORGANISATION_TYPE.NOVATIQ);
          this.orgTypes.set(this.sortOrgTypes(customerOrgTypes));
        },
        error: () => this.showError('errorLoadingData')
      });
  }

  onOrgTypeChange(orgType: string | null): void {
    this.selectedOrgType.set(orgType);
    this.selectedOrganisationId.set(null);
    this.selectedRoleId.set(null);
    this.organisations.set([]);
    this.roles.set([]);
    this.apiError.set(null);
  }

  onOrganisationChange(organisationId: string | null): void {
    this.selectedOrganisationId.set(organisationId);
    this.selectedRoleId.set(null);
    this.roles.set([]);
    this.apiError.set(null);

    if (!organisationId) {
      return;
    }

    const orgTypeId = this.resolveOrgTypeId();
    if (!orgTypeId) {
      return;
    }

    this.loadingRoles.set(true);
    this.userRoleService
      .getUserRoles(orgTypeId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loadingRoles.set(false))
      )
      .subscribe({
        next: (response) => this.roles.set((response as IURMRole).roles ?? []),
        error: () => this.showError('errorLoadingData')
      });
  }

  onRoleChange(roleId: number | null): void {
    this.selectedRoleId.set(roleId);
    this.apiError.set(null);
  }

  onProceed(): void {
    const step = this.activeStep();

    if (step === 1) {
      if (!this.canProceedStep1()) {
        return;
      }
      this.loadOrganisations();
      return;
    }

    if (step === 2) {
      if (!this.canProceedStep2()) {
        return;
      }
      this.activeStep.set(3);
      return;
    }

    if (step === 3) {
      this.submitShadowSession();
    }
  }

  onBack(): void {
    const step = this.activeStep();
    if (step === 2) {
      this.activeStep.set(1);
    } else if (step === 3) {
      this.activeStep.set(2);
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  orgTypeTranslationKey(orgType: string): string {
    return `organisationTypes.${orgType}`;
  }

  organisationFriendlyName(organisation: OrganisationApiItem): string {
    return organisation.friendly_name?.trim() || organisation.name;
  }

  roleTranslationKey(roleName: string): string {
    return `roles.${roleName}`;
  }

  getRoleLabel(roleName: string): string {
    const key = this.roleTranslationKey(roleName);
    const translated = this.translate.instant(key);
    return translated === key ? roleName : translated;
  }

  private loadOrganisations(): void {
    const orgType = this.selectedOrgType();
    if (!orgType) {
      return;
    }

    this.loadingOrganisations.set(true);
    this.apiError.set(null);
    this.userManagementService
      .getOrganisations(orgType, 'Active')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loadingOrganisations.set(false))
      )
      .subscribe({
        next: (response) => {
          this.organisations.set(this.sortOrganisations(response.organisations ?? []));
          this.activeStep.set(2);
        },
        error: () => this.showError('errorLoadingData')
      });
  }

  private submitShadowSession(): void {
    const organisationId = this.selectedOrganisationId();
    const roleId = this.selectedRoleId();

    if (!organisationId || roleId === null) {
      return;
    }

    this.submitting.set(true);
    this.apiError.set(null);
    this.authorizationHelper
      .enterShadowSession(organisationId, roleId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error) => {
          this.apiError.set(error?.message ?? this.translate.instant('shadowSessionError'));
          return of(undefined);
        }),
        finalize(() => this.submitting.set(false))
      )
      .subscribe();
  }

  private resolveOrgTypeId(): number | null {
    const orgType = this.selectedOrgType();
    if (!orgType) {
      return null;
    }

    const metadata = this.orgTypes().find((item) => item.value === orgType || item.name === orgType);
    return metadata?.id ?? null;
  }

  private sortOrgTypes(orgTypes: IBaseMetadata[]): IBaseMetadata[] {
    return [...orgTypes].sort((a, b) => {
      const orderA = SHADOW_ORG_TYPE_ORDER[a.value ?? ''] ?? Number.MAX_SAFE_INTEGER;
      const orderB = SHADOW_ORG_TYPE_ORDER[b.value ?? ''] ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });
  }

  private sortOrganisations(organisations: OrganisationApiItem[]): OrganisationApiItem[] {
    return [...organisations].sort((a, b) =>
      this.organisationFriendlyName(a).localeCompare(this.organisationFriendlyName(b), undefined, {
        sensitivity: 'base'
      })
    );
  }

  private showError(messageKey: string): void {
    this.messageService.sendMessage(this.translate.instant(messageKey), 'error');
  }
}
